import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Container, Row, Col } from 'react-bootstrap';
import AOS from 'aos';
import {
  FaRobot, FaBuilding, FaGraduationCap, FaHandshake, FaUserTie,
  FaShoppingCart, FaNetworkWired, FaTruck, FaArrowRight, FaCheckCircle, FaChevronRight
} from 'react-icons/fa';
import productsData from '../data/products.json';

const ICON_MAP = {
  FaRobot: <FaRobot />, FaBuilding: <FaBuilding />, FaGraduationCap: <FaGraduationCap />,
  FaHandshake: <FaHandshake />, FaUserTie: <FaUserTie />, FaShoppingCart: <FaShoppingCart />,
  FaNetworkWired: <FaNetworkWired />, FaTruck: <FaTruck />,
};

const Products = () => {
  useEffect(() => { AOS.init({ duration: 900, once: true, offset: 80 }); }, []);

  const featured = productsData.find(p => p.id === 1);
  const rest = productsData.filter(p => p.id !== 1);

  return (
    <div style={{ background: 'var(--color-bg)' }}>
      <section className="page-hero">
        <div className="page-hero-overlay" />
        <Container>
          <div className="page-hero-content" data-aos="fade-up">
            <div className="breadcrumb-vk"><Link to="/">Home</Link> / <span>Products</span></div>
            <h1 className="page-hero-title">
              Our Enterprise <span>SaaS Products</span>
            </h1>
            <p className="page-hero-desc">
              A complete ecosystem of AI-powered, cloud-native SaaS products built for every business function — from ERP and LMS to CRM, HRMS, eCommerce, and more.
            </p>
          </div>
        </Container>
      </section>

      {/* Featured Product */}
      {featured && (
        <section className="section-py" style={{ background: 'var(--color-bg)' }}>
          <Container>
            <Row className="align-items-center g-5">
              <Col lg={6} data-aos="fade-right">
                <div style={{ background: `${featured.badgeColor}08`, borderRadius: 'var(--radius-xl)', padding: '3rem', border: `1px solid ${featured.badgeColor}20` }}>
                  <div style={{ width: 72, height: 72, borderRadius: 18, background: `${featured.badgeColor}15`, color: featured.badgeColor, display: 'grid', placeItems: 'center', fontSize: '2rem', marginBottom: '1.5rem' }}>
                    {ICON_MAP[featured.icon]}
                  </div>
                  <span style={{ display: 'inline-block', background: featured.badgeColor, color: '#fff', fontSize: '0.75rem', fontWeight: 700, padding: '4px 14px', borderRadius: 999, marginBottom: '1rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{featured.badge}</span>
                  <h2 style={{ fontSize: '1.75rem', fontWeight: 900, color: 'var(--color-text)', marginBottom: '0.75rem' }}>{featured.name}</h2>
                  <p style={{ fontSize: '1.125rem', color: featured.badgeColor, fontWeight: 600, marginBottom: '1rem' }}>{featured.tagline}</p>
                  <p style={{ color: 'var(--color-text-muted)', lineHeight: 1.75, marginBottom: '1.5rem' }}>{featured.description}</p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.625rem' }}>
                    {featured.features.map((f, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.875rem', color: 'var(--color-text-muted)' }}>
                        <FaCheckCircle style={{ color: featured.badgeColor, flexShrink: 0 }} /> {f}
                      </div>
                    ))}
                  </div>
                </div>
              </Col>
              <Col lg={6} data-aos="fade-left">
                <span className="section-badge">Featured Product</span>
                <h2 className="section-title">The Intelligence<br /><span>Layer for Your Business</span></h2>
                <div className="gradient-line" />
                <p style={{ color: 'var(--color-text-muted)', lineHeight: 1.75, marginBottom: '1.5rem' }}>
                  SIKGEN AI is our flagship artificial intelligence platform that brings predictive analytics, natural language processing, and intelligent automation to your enterprise workflows.
                </p>
                {['Reduce manual effort by up to 70%', 'Real-time business intelligence', 'Seamless integration with existing systems', 'GDPR-compliant & enterprise-grade security'].map((f, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: '0.9375rem', color: 'var(--color-text)', marginBottom: 10 }}>
                    <FaCheckCircle style={{ color: '#059669', fontSize: '0.875rem', flexShrink: 0 }} /> {f}
                  </div>
                ))}
                <Link to="/contact" className="btn-primary-vk" style={{ marginTop: '1.5rem' }}>
                  Request Demo <FaArrowRight />
                </Link>
              </Col>
            </Row>
          </Container>
        </section>
      )}

      {/* All Products Grid */}
      <section className="section-py" style={{ background: 'var(--color-bg-alt)' }}>
        <Container>
          <div className="section-header text-center" data-aos="fade-up">
            <span className="section-badge">All Products</span>
            <h2 className="section-title">Complete <span>Product Suite</span></h2>
            <p className="section-subtitle">8+ enterprise-grade SaaS products, all built in-house by the Vkomak team.</p>
          </div>
          <Row className="g-4">
            {rest.map((product, i) => (
              <Col lg={4} md={6} key={product.id} data-aos="fade-up" data-aos-delay={i * 80}>
                <div className="vk-card" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                    <div style={{ width: 52, height: 52, borderRadius: 12, background: `${product.badgeColor}12`, color: product.badgeColor, display: 'grid', placeItems: 'center', fontSize: '1.375rem' }}>
                      {ICON_MAP[product.icon]}
                    </div>
                    <span style={{ background: product.badgeColor, color: '#fff', fontSize: '0.6875rem', fontWeight: 700, padding: '3px 10px', borderRadius: 999, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{product.badge}</span>
                  </div>
                  <h4 style={{ fontSize: '1.0625rem', fontWeight: 700, color: 'var(--color-text)', marginBottom: '0.25rem' }}>{product.name}</h4>
                  <p style={{ fontSize: '0.875rem', color: product.badgeColor, fontWeight: 600, marginBottom: '0.625rem' }}>{product.tagline}</p>
                  <p style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)', lineHeight: 1.6, flex: 1, marginBottom: '1rem' }}>{product.description}</p>
                  <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: '0.875rem' }}>
                    {product.features.map((f, fi) => (
                      <div key={fi} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8125rem', color: 'var(--color-text-muted)', marginBottom: 4 }}>
                        <FaChevronRight style={{ color: product.badgeColor, fontSize: '0.625rem', flexShrink: 0 }} /> {f}
                      </div>
                    ))}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '1rem' }}>
                    <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#059669' }}>● {product.status}</span>
                    <Link to="/contact" style={{ fontSize: '0.8125rem', fontWeight: 600, color: product.badgeColor, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 5 }}>
                      Request Demo <FaArrowRight style={{ fontSize: '0.625rem' }} />
                    </Link>
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      <section className="cta-section">
        <Container className="text-center" data-aos="zoom-in">
          <h2 className="cta-title">Need a <span>Custom Product?</span></h2>
          <p className="cta-desc">We build bespoke SaaS products tailored to your specific business requirements.</p>
          <Link to="/contact" className="btn-primary-vk">Let's Build Together <FaArrowRight /></Link>
        </Container>
      </section>
    </div>
  );
};

export default Products;
